import { ethers } from "ethers";
import FHELotteryABI from "../abi/FHELottery.json";

export interface LotteryInfo {
  id: bigint;
  creator: string;
  name: string;
  maxParticipants: bigint;
  participantCount: bigint;
  isActive: boolean;
  isDrawn: boolean;
  winner: string;
}

// Contract addresses for different networks
const CONTRACT_ADDRESSES: Record<number, string> = {
  31337: "0xe7f1725E7734CE288F8367e1Bb143E90bb3F0512", // localhost (Hardhat) - from deployments/localhost/FHELottery.json
  11155111: "0xAe3a33b4E9F75D697291772cc2Dd651AC9E818fb", // Sepolia testnet - deployed on Sepolia
};

// Get contract address based on chainId
// Falls back to environment variable or localhost address if chainId is not found
export function getContractAddress(chainId?: number): string {
  // If chainId is provided and exists in our mapping, use it
  if (chainId && CONTRACT_ADDRESSES[chainId]) {
    return CONTRACT_ADDRESSES[chainId];
  }
  
  // Fallback to environment variable
  if (import.meta.env.VITE_CONTRACT_ADDRESS) {
    return import.meta.env.VITE_CONTRACT_ADDRESS;
  }
  
  // Default to localhost address
  return CONTRACT_ADDRESSES[31337];
}

// Default contract address (for backward compatibility)
// Use getContractAddress(chainId) instead when chainId is available
export const CONTRACT_ADDRESS = CONTRACT_ADDRESSES[31337];

export async function getContract(
  signerOrProvider: ethers.Signer | ethers.Provider,
  chainId?: number
) {
  const contractAddress = getContractAddress(chainId);
  if (!contractAddress) {
    throw new Error("Contract address not set. Please deploy the contract first.");
  }
  return new ethers.Contract(contractAddress, FHELotteryABI, signerOrProvider);
}

export async function createLottery(
  signer: ethers.Signer,
  name: string,
  maxParticipants: number,
  chainId?: number
): Promise<bigint> {
  const contract = await getContract(signer, chainId);
  const tx = await contract.createLottery(name, maxParticipants);
  const receipt = await tx.wait();
  
  // Get lottery ID from event
  const event = receipt.logs.find((log: any) => {
    try {
      const parsed = contract.interface.parseLog(log);
      return parsed?.name === "LotteryCreated";
    } catch {
      return false;
    }
  });
  
  if (event) {
    const parsed = contract.interface.parseLog(event);
    return parsed?.args[0] || BigInt(0);
  }
  
  // Fallback: get latest lottery count
  const count = await contract.getLotteryCount();
  return count;
}

export async function registerParticipant(
  signer: ethers.Signer,
  lotteryId: bigint,
  encryptedHandle: string,
  inputProof: string,
  chainId?: number
): Promise<void> {
  const contract = await getContract(signer, chainId);
  const tx = await contract.registerParticipant(lotteryId, encryptedHandle, inputProof);
  await tx.wait();
}

export async function drawWinner(
  signer: ethers.Signer,
  lotteryId: bigint,
  chainId?: number
): Promise<void> {
  const contract = await getContract(signer, chainId);
  const tx = await contract.drawWinner(lotteryId);
  await tx.wait();
}

export async function getLotteryInfo(
  provider: ethers.Provider,
  lotteryId: bigint,
  chainId?: number
): Promise<LotteryInfo> {
  const contract = await getContract(provider as any, chainId);
  const info = await contract.getLotteryInfo(lotteryId);
  return {
    id: info[0],
    creator: info[1],
    name: info[2],
    maxParticipants: info[3],
    participantCount: info[4],
    isActive: info[5],
    isDrawn: info[6],
    winner: info[7],
  };
}

export async function getWinningNumber(
  provider: ethers.Provider,
  lotteryId: bigint,
  chainId?: number
): Promise<string> {
  const contract = await getContract(provider as any, chainId);
  return await contract.getWinningNumber(lotteryId);
}

export async function isParticipant(
  provider: ethers.Provider,
  lotteryId: bigint,
  address: string,
  chainId?: number
): Promise<boolean> {
  const contract = await getContract(provider as any, chainId);
  return await contract.isParticipant(lotteryId, address);
}

export async function getLotteryCount(
  provider: ethers.Provider,
  chainId?: number
): Promise<bigint> {
  const contract = await getContract(provider as any, chainId);
  return await contract.getLotteryCount();
}

